/* eslint-disable no-unused-vars */
// PackageSearchControls.jsx
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField
} from '@mui/material';
import React from 'react';

export default function PackageSearchControls({
  searchValue,
  setSearchValue,
  sortByList,
  sortBy,
  setSortBy,
  sortOrder,
  setSortOrder,
  dateFilter,
  setDateFilter,
  filters,
  setFilters,
  filterDraft,
  setFilterDraft,
  showAdvancedSearch,
  setShowAdvancedSearch,
  handleSearchClick,
  fetchAllPackages,
  setPagination,
  filterOptions,
  addFilter,
  removeFilter,
  formatFieldLabel
}) {
  return (
    <>
      <Box mb={2} display="flex" alignItems="center" flexWrap="wrap">
        <TextField
          label="Tracking Number"
          variant="outlined"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          style={{ marginRight: 8, marginBottom: 8 }}
        />

        {/* SORT BY */}
        <FormControl
          style={{ minWidth: 160, marginRight: 8, marginBottom: 8 }}
          variant="outlined"
        >
          {sortBy !== 'default' && (
            <InputLabel id="sort-by-label">Sort By</InputLabel>
          )}
          <Select
            labelId={sortBy !== 'default' ? 'sort-by-label' : undefined}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            displayEmpty
            label={sortBy !== 'default' ? 'Sort By' : undefined}
          >
            <MenuItem value="default">
              <em>Sort By</em>
            </MenuItem>
            {sortByList.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* SORT ORDER */}
        <FormControl
          style={{ minWidth: 160, marginRight: 8, marginBottom: 8 }}
          variant="outlined"
        >
          {sortOrder !== 'default' && (
            <InputLabel id="sort-order-label">Order</InputLabel>
          )}
          <Select
            labelId={sortOrder !== 'default' ? 'sort-order-label' : undefined}
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            displayEmpty
            label={sortOrder !== 'default' ? 'Order' : undefined}
          >
            <MenuItem value="default">
              <em>Order</em>
            </MenuItem>
            <MenuItem value="asc">Ascending</MenuItem>
            <MenuItem value="desc">Descending</MenuItem>
          </Select>
        </FormControl>

        {/* DATE FILTER */}
        <FormControl
          style={{ minWidth: 160, marginRight: 8, marginBottom: 8 }}
          variant="outlined"
        >
          {dateFilter !== 'default' && (
            <InputLabel id="date-filter-label">Date Filter</InputLabel>
          )}
          <Select
            labelId={dateFilter !== 'default' ? 'date-filter-label' : undefined}
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            displayEmpty
            label={dateFilter !== 'default' ? 'Date Filter' : undefined}
          >
            <MenuItem value="default">
              <em>Date Filter ↑↓</em>
            </MenuItem>
            <MenuItem value="this_week">This Week</MenuItem>
            <MenuItem value="this_month">This Month</MenuItem>
            <MenuItem value="this_year">This Year</MenuItem>
            <MenuItem value="last_week">Last week</MenuItem>
            <MenuItem value="last_month">Last month</MenuItem>
            <MenuItem value="last_year">Last year</MenuItem>
            <MenuItem value="last_seven_days">Last 7 days</MenuItem>
            <MenuItem value="last_thirty_days">Last 30 days</MenuItem>
            <MenuItem value="last_sixty_days">Last 60 days</MenuItem>
            <MenuItem value="last_ninety_days">Last 90 days</MenuItem>
            <MenuItem value="last_one_eighty_days">Last 180 days</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="text"
          onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
          style={{ marginBottom: 8, height: 56, padding: '6px 16px' }}
        >
          {showAdvancedSearch ? 'Hide Advanced Search' : 'Advanced Search'}
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={handleSearchClick}
          disabled={!searchValue && filters.length === 0}
          style={{
            marginLeft: 8,
            marginRight: 8,
            marginBottom: 8,
            height: 56,
            padding: '6px 16px'
          }}
        >
          Search
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={() => {
            setSearchValue('');
            setFilters([]);
            setDateFilter('default');
            setSortBy('default');
            setSortOrder('default');
            setPagination({ currentPage: 1, totalPages: 1, limit: 20 });
            setShowAdvancedSearch(false);
            setFilterDraft({ field: '', value: '' });
            localStorage.removeItem('searchValue');
            localStorage.removeItem('advancedSearchFilters');
            fetchAllPackages();
          }}
          style={{
            marginRight: 8,
            marginBottom: 8,
            height: 56,
            padding: '6px 16px'
          }}
        >
          Reset
        </Button>
      </Box>

      <Collapse in={showAdvancedSearch}>
        <Box mb={2} display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FormControl style={{ minWidth: 200 }} variant="outlined">
            {filterDraft.field && (
              <InputLabel id="field-filter-label">Select Field</InputLabel>
            )}
            <Select
              labelId={filterDraft.field ? 'field-filter-label' : undefined}
              value={filterDraft.field}
              onChange={(e) =>
                setFilterDraft((prev) => ({ ...prev, field: e.target.value }))
              }
              displayEmpty
              label={filterDraft.field ? 'Select Field' : undefined}
            >
              <MenuItem value="" disabled>
                Select Field
              </MenuItem>
              {filterOptions.map(({ value, label }) => {
                const alreadyUsed = filters.some((f) => f.field === value);
                return (
                  <MenuItem key={value} value={value} disabled={alreadyUsed}>
                    {alreadyUsed ? (
                      <CheckBoxIcon
                        fontSize="small"
                        style={{ marginRight: 8 }}
                      />
                    ) : (
                      <CheckBoxOutlineBlankIcon
                        fontSize="small"
                        style={{ marginRight: 8 }}
                      />
                    )}
                    {label}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {filterDraft.field === 'direction_type' ? (
            <FormControl style={{ minWidth: 200 }} variant="outlined">
              {filterDraft.value && (
                <InputLabel id="direction-type-label">
                  Direction Type
                </InputLabel>
              )}
              <Select
                labelId={filterDraft.value ? 'direction-type-label' : undefined}
                value={filterDraft.value}
                onChange={(e) =>
                  setFilterDraft((prev) => ({ ...prev, value: e.target.value }))
                }
                displayEmpty
                label={filterDraft.value ? 'Direction Type' : undefined}
              >
                <MenuItem value="" disabled>
                  Select Direction Type
                </MenuItem>
                <MenuItem value="incoming">Incoming</MenuItem>
                <MenuItem value="outgoing">Outgoing</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <TextField
              label="Value"
              variant="outlined"
              value={filterDraft.value}
              onChange={(e) =>
                setFilterDraft((prev) => ({ ...prev, value: e.target.value }))
              }
            />
          )}

          <Button
            variant="outlined"
            onClick={addFilter}
            style={{ height: 56, padding: '6px 16px' }}
          >
            + Add Filter
          </Button>
        </Box>

        {filters.length > 0 && (
          <Box mb={2} display="flex" flexWrap="wrap" gap={1}>
            {filters.map((filter) => (
              <Chip
                key={filter.field}
                label={`${formatFieldLabel(filter.field)}: ${filter.value}`}
                onDelete={() => removeFilter(filter.field)}
                color="primary"
              />
            ))}
          </Box>
        )}
      </Collapse>
    </>
  );
}
